/** Вкладка «Сверка» WB: начисления витрины против шапок отчётов реализации WB — по месяцам
 *  (месяц — по началу отчёта) и столбцам ЛК в порядке `sort`; раскрытие месяца → отчёты WB
 *  месяца → строки отчёта. Знак — как в ЛК WB (удержания положительными). Отчёт, чьего
 *  (месяц, юрлицо) нет среди месяцев витрины, — расхождение с контрактом: отдельным блоком. */
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import type { WbRecon, WbReconStatus, WbReport, WbReportRow } from "@/api/mpAnalytics";
import { cn } from "@/lib/utils";
import { diffText, fmtDate, monthLabel, rub } from "./format";
import { ErrorBox, Loading } from "./shared";
import { tdNum, thClass } from "./ui";

const REPORT_TYPE: Record<number, string> = { 1: "Основной", 2: "По выкупам" };
const PERIOD: Record<WbReport["period"], string> = { daily: "ежедневный", weekly: "недельный" };

/** Тип отчёта WB; незнакомый код — как есть (с номером), а не «Основной». */
function reportTypeLabel(t: number | null): string {
  if (t === null) return "тип не указан";
  return REPORT_TYPE[t] ?? `тип ${t}`;
}

/** Строки по `sort`, при равном — по подписи (порядок столбцов ЛК WB). */
function bySort<T extends { sort: number; label: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label, "ru"));
}

const money = (v: number | null): string => (v === null ? "—" : rub(v));
const isOff = (v: number | null): boolean => v !== null && Math.abs(v) >= 0.005;

export function StatusBadge({
  status,
  unexplained = null,
}: {
  status: WbReconStatus;
  /** сумма модулей необъяснённых расхождений — у месяца с mismatch */
  unexplained?: number | null;
}): React.ReactElement {
  if (status === "ok") {
    return <span className="text-emerald-700 dark:text-emerald-400">✓ сошлось</span>;
  }
  if (status === "pending") {
    return <span className="text-amber-700 dark:text-amber-400">ждёт недельного отчёта</span>;
  }
  if (status === "mismatch") {
    return (
      <span className="font-medium text-red-600 dark:text-red-400">
        ✗ расхождение{unexplained !== null && isOff(unexplained) ? ` ${diffText(unexplained)}` : ""}
      </span>
    );
  }
  return <span className="text-amber-700 dark:text-amber-400">не с чем сверить</span>;
}

/** Ключ отчёта: номер отчёта WB уникален только вместе с юрлицом и периодом (daily/weekly). */
function reportKey(r: WbReport): string {
  return `${r.legal_name}|${r.report_id}|${r.period}`;
}

const monthKey = (month: string, legal: string): string => `${month}\u0001${legal}`;

/** Отчёты, чьего (месяц, юрлицо) нет среди `months` витрины. */
function orphanReports(d: WbRecon): WbReport[] {
  const known = new Set(d.months.map((m) => monthKey(m.month, m.legal_name)));
  return d.reports.filter((r) => !known.has(monthKey(r.month, r.legal_name)));
}

function DiffCell({
  diff,
  note,
  muted = false,
}: {
  diff: number | null;
  note: string | null;
  muted?: boolean;
}): React.ReactElement {
  const off = isOff(diff);
  return (
    <td
      className={cn(
        tdNum,
        off &&
          !muted &&
          (note ? "text-amber-700 dark:text-amber-400" : "font-medium text-red-600 dark:text-red-400"),
      )}
    >
      {diff === null ? "—" : diffText(diff)}
    </td>
  );
}

export function WbReconTab({
  query,
}: {
  query: { data?: WbRecon; isLoading: boolean; isError: boolean; error: unknown };
}): React.ReactElement {
  const d = query.data;
  const orphans = d ? orphanReports(d) : [];
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (k: string): void =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  return (
    <div className="space-y-3">
      {query.isLoading && <Loading text="Загружаю сверку с отчётами WB…" />}
      {query.isError && <ErrorBox error={query.error} what="сверку с отчётами WB" />}
      {d && (
        <div className="overflow-x-auto rounded-md border bg-card">
          <table className="w-full min-w-[860px] text-xs" data-testid="wb-recon">
            <thead className="bg-muted/40 text-[11px]">
              <tr>
                <th className={cn(thClass, "text-left")}>Месяц</th>
                <th className={cn(thClass, "text-left")}>Юрлицо</th>
                <th className={cn(thClass, "text-left")}>Столбец ЛК</th>
                <th className={thClass}>Начислено</th>
                <th className={thClass}>Шапка WB</th>
                <th className={thClass}>Разница</th>
                <th className={cn(thClass, "text-left")}>Примечание / статус</th>
              </tr>
            </thead>
            {d.months.length === 0 && orphans.length === 0 && (
              <tbody>
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-muted-foreground">
                    Отчётов WB за месяцы периода нет.
                  </td>
                </tr>
              </tbody>
            )}
            {d.months.map((m) => {
              const k = monthKey(m.month, m.legal_name);
              const rows = bySort(
                d.rows.filter((r) => r.month === m.month && r.legal_name === m.legal_name),
              );
              const reports = d.reports.filter(
                (r) => r.month === m.month && r.legal_name === m.legal_name,
              );
              const isOpen = open.has(k);
              return (
                <tbody key={k} className="border-t-2" data-testid="wb-recon-month">
                  <tr className="bg-muted/20 font-medium">
                    <td className="px-2 py-1">{monthLabel(m.month)}</td>
                    <td className="px-2 py-1">{m.legal_name}</td>
                    <td className="px-2 py-1">
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() => toggle(k)}
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        {isOpen ? (
                          <ChevronDown className="size-3.5" aria-hidden />
                        ) : (
                          <ChevronRight className="size-3.5" aria-hidden />
                        )}
                        Отчёты WB ({reports.length})
                      </button>
                    </td>
                    <td className={tdNum}>{money(m.accrual)}</td>
                    <td className={tdNum}>{money(m.statement)}</td>
                    <DiffCell diff={m.diff} note={null} muted={m.status !== "mismatch"} />
                    <td className="px-2 py-1">
                      <StatusBadge status={m.status} unexplained={m.unexplained_abs} />
                    </td>
                  </tr>
                  {rows.map((r) => (
                    <tr key={`${r.sort}|${r.label}`} className="border-t" data-testid="wb-recon-col">
                      <td />
                      <td />
                      <td className="px-2 py-1 pl-6">{r.label}</td>
                      <td className={tdNum}>{money(r.accrual)}</td>
                      <td className={tdNum}>{r.has_header ? money(r.statement) : "—"}</td>
                      <DiffCell diff={r.diff} note={r.note} muted={!r.has_header} />
                      <td className="px-2 py-1 text-muted-foreground">{r.note ?? ""}</td>
                    </tr>
                  ))}
                  {isOpen && (
                    <tr>
                      <td colSpan={7} className="bg-muted/10 px-2 py-2 pl-6">
                        <ReportList reports={reports} />
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
          </table>
        </div>
      )}
      {orphans.length > 0 && (
        <div
          className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs dark:border-amber-800 dark:bg-amber-950"
          data-testid="wb-orphan-reports"
        >
          <div className="font-medium text-amber-800 dark:text-amber-300">
            Отчёты без месячной строки витрины ({orphans.length})
          </div>
          <div className="text-amber-800 dark:text-amber-300">
            Витрина отдала отчёты WB за месяц и юрлицо, для которых нет строки сверки месяца, —
            это расхождение с контрактом витрины (mart.mp_finance_recon).
          </div>
          <div className="rounded-md border bg-card p-2">
            <ReportList reports={orphans} showOwner />
          </div>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Сверка — с итогами отчётов реализации WB (шапкой отчёта), знак — как в ЛК: удержания
        положительными. Месяц сверки — по началу отчёта WB, а P&amp;L — по дате операции, поэтому
        суммы месяцев сверки и сводки могут отличаться.
      </p>
    </div>
  );
}

function ReportList({
  reports,
  showOwner = false,
}: {
  reports: WbReport[];
  /** показать месяц и юрлицо отчёта (блок отчётов без месячной строки) */
  showOwner?: boolean;
}): React.ReactElement {
  const [open, setOpen] = useState<string | null>(null);
  if (reports.length === 0) {
    return <div className="text-muted-foreground">Отчётов WB за месяц нет.</div>;
  }
  return (
    <table className="w-full text-xs" data-testid="wb-reports">
      <thead className="text-[11px]">
        <tr>
          <th className={cn(thClass, "text-left")}>Отчёт</th>
          {showOwner && (
            <>
              <th className={cn(thClass, "text-left")}>Месяц</th>
              <th className={cn(thClass, "text-left")}>Юрлицо</th>
            </>
          )}
          <th className={cn(thClass, "text-left")}>Тип</th>
          <th className={cn(thClass, "text-left")}>Период</th>
          <th className={thClass}>Строк</th>
          <th className={cn(thClass, "text-left")}>Статус</th>
        </tr>
      </thead>
      <tbody>
        {reports.map((r) => {
          const id = String(r.report_id);
          const k = reportKey(r);
          const isOpen = open === k;
          return (
            <React.Fragment key={k}>
              <tr className="border-t" data-testid="wb-report">
                <td className="px-2 py-1">
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? null : k)}
                    className="inline-flex items-center gap-1 tabular-nums text-primary hover:underline"
                  >
                    {isOpen ? (
                      <ChevronDown className="size-3.5" aria-hidden />
                    ) : (
                      <ChevronRight className="size-3.5" aria-hidden />
                    )}
                    № {id}
                  </button>
                </td>
                {showOwner && (
                  <>
                    <td className="px-2 py-1">{monthLabel(r.month)}</td>
                    <td className="px-2 py-1">{r.legal_name}</td>
                  </>
                )}
                <td className="px-2 py-1">{reportTypeLabel(r.report_type)}</td>
                <td className="px-2 py-1">
                  {PERIOD[r.period]}, {fmtDate(r.date_from)} — {fmtDate(r.date_to)}
                </td>
                <td className={tdNum}>{r.realization_rows}</td>
                <td className="px-2 py-1">
                  <StatusBadge status={r.status} />
                </td>
              </tr>
              {isOpen && (
                <tr>
                  <td colSpan={showOwner ? 7 : 5} className="px-2 py-1 pl-6">
                    <ReportRows rows={r.rows} hasHeader={r.has_header} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function ReportRows({
  rows,
  hasHeader,
}: {
  rows: WbReportRow[];
  hasHeader: boolean;
}): React.ReactElement {
  return (
    <table className="w-full text-xs" data-testid="wb-report-rows">
      <thead className="text-[11px]">
        <tr>
          <th className={cn(thClass, "text-left")}>Столбец ЛК</th>
          <th className={thClass}>Начислено</th>
          <th className={thClass}>Шапка WB</th>
          <th className={thClass}>Разница</th>
          <th className={cn(thClass, "text-left")}>Примечание</th>
        </tr>
      </thead>
      <tbody>
        {bySort(rows).map((r) => (
          <tr key={`${r.sort}|${r.label}`} className="border-t">
            <td className="px-2 py-1">{r.label}</td>
            <td className={tdNum}>{money(r.accrual)}</td>
            <td className={tdNum}>{hasHeader ? money(r.statement) : "—"}</td>
            <DiffCell diff={r.diff} note={r.note} muted={!hasHeader} />
            <td className="px-2 py-1 text-muted-foreground">{r.note ?? ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
